<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import Container from '~/components/layout/Container.vue'
import useQuickOfferModalStore from '~/stores/modals/useQuickOfferModal'
import type { ReferenceLandingPageItem } from '~/types/api/ReferenceDetail'

const props = defineProps<{
  landingPageItem: ReferenceLandingPageItem
}>()

const quickOfferModalStore = useQuickOfferModalStore()

const imageData = getImageSrcSizesData(props.landingPageItem.image, {
  includeGenericSrcset: false,
  defaultFormat: '100x200',
  srcsetToGenerate: [
    ['100x200', '1x'],
    ['200x400', '2x'],
    ['400x800', '3x'],
  ],
})
</script>

<template>
  <div class="c-client-rating">
    <Container class="c-client-rating__container">
      <h2 class="c-client-rating__title">
        {{ landingPageItem.name }}
      </h2>

      <div class="c-client-rating__client">
        <div class="sc-image">
          <img
            :src="imageData.src"
            :srcset="imageData.srcset"
            :alt="landingPageItem.module_data.name"
          />
        </div>

        <div class="sc-name">
          <strong>{{ landingPageItem.module_data.name }}</strong>
          <span v-if="landingPageItem.module_data.position">{{
            landingPageItem.module_data.position
          }}</span>
        </div>

        <div class="sc-bg-quotes">
          <img src="~/assets/svg-icons/quotes.svg?url" />
        </div>
      </div>

      <div class="c-client-rating__rating">
        <div class="sc-description" v-html="landingPageItem.description" />
      </div>

      <div class="c-client-rating__offer">
        <div class="sc-title">
          {{ $t('pages.references.detail.parts.clientRating.offer.title') }}
        </div>

        <IButton
          class="has-icon"
          color="secondary"
          :block="true"
          @click="quickOfferModalStore.open()"
        >
          {{
            $t('pages.references.detail.parts.clientRating.offer.buttonText')
          }}

          <Icon name="arrow-right-thick" />
        </IButton>
      </div>
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/content' as contentMixin;

.c-client-rating {
  margin-bottom: 120px;

  @include breakpoint-down('lg') {
    margin-bottom: 100px;
  }

  @include breakpoint-down('md') {
    margin-bottom: 60px;
  }

  &__container {
    display: grid;
    grid-template:
      'title title title' auto
      'client rating offer' 1fr / auto 1fr 400px;
    align-items: center;
    gap: 50px 40px;

    @include breakpoint-down('xl') {
      grid-template-columns: auto 1fr 320px;
      gap: 32px;
    }

    @include breakpoint-down('lg') {
      grid-template:
        'title title' auto
        'client rating' 1fr
        'offer offer' 1fr / auto 1fr;
    }

    @include breakpoint-down('sm') {
      grid-template:
        'title' auto
        'client' auto
        'rating' auto
        'offer' auto / 1fr;
    }
  }

  &__title {
    grid-area: title;
    font-size: 40px;
    line-height: 1;
    margin: 0;
  }

  &__client {
    grid-area: client;
    display: flex;
    align-items: center;
    gap: 24px;
    position: relative;

    &::after {
      content: '';
      display: block;
      width: 1px;
      background-color: var(--color-border);
      height: 70px;
      margin-left: 16px;

      @include breakpoint-down('xl') {
        margin-left: 8px;
      }

      @include breakpoint-down('sm') {
        display: none;
      }
    }

    .sc-image {
      width: 100px;
      height: 100px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }

    .sc-name {
      font-size: 20px;
      line-height: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-width: 200px;

      strong {
        color: var(--color-primary);
      }

      span {
        font-size: 18px;
      }
    }

    .sc-bg-quotes {
      position: absolute;
      z-index: -1;
      left: 54px;
      top: -10px;

      @include breakpoint-down('md') {
        top: -7px;
      }

      img {
        width: 190px;
        height: 144px;

        @include breakpoint-down('md') {
          width: 150px;
          height: 114px;
        }
      }
    }
  }

  &__rating {
    grid-area: rating;

    :deep(.sc-description) {
      @include contentMixin.content;
    }
  }

  &__offer {
    grid-area: offer;
    padding: 24px 40px 32px;
    border: 1px solid var(--color-border);

    @include breakpoint-down('xl') {
      padding: 16px 30px 24px;
    }

    .sc-title {
      font-size: 20px;
      line-height: 1.2;
      font-weight: 500;
      margin-bottom: 12px;

      @include breakpoint-down('xl') {
        font-size: 17px;
      }
    }

    :deep(.button) {
      --button--min-height: 60px;
      --button--font-size: 18px;
      --button--font-weight: 600;
      --button--icon--font-size: 25px;

      @include breakpoint-down('xl') {
        --button--min-height: 52px;
        --button--font-size: 16px;
      }
    }
  }
}
</style>
